import { useState, useEffect } from "react";
import {
  Users, DollarSign, AlertTriangle, Car, Shield, Home, User, Calendar, CreditCard,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import MonthlyChart from "./MonthlyChart";
import AlertsWidget from "./AlertsWidget";
import ReferralsWidget from "./ReferralsWidget";
import DoletasWidget from "./DoletasWidget";
import DuplicatesAlert from "./DuplicatesAlert";

function formatValue(v) {
  if (v == null || v === 0) return "R$ 0";
  if (v >= 1000) return "R$ " + (v / 1000).toFixed(1) + "k";
  return "R$ " + v.toFixed(0);
}

function getInitials(name) {
  if (!name) return "U";
  return name.split(" ").map(function(n) { return n[0]; }).join("").slice(0, 2).toUpperCase();
}

function getBadge(conv) {
  if (conv >= 70) return { label: "Top", cls: "bg-emerald-100 text-emerald-700" };
  if (conv >= 50) return { label: "Bom", cls: "bg-blue-100 text-blue-700" };
  return { label: "Regular", cls: "bg-amber-100 text-amber-700" };
}

var AVATAR_COLORS = ["bg-[#3B82F6]", "bg-[#F26A4B]", "bg-amber-500", "bg-purple-500", "bg-emerald-500", "bg-pink-500"];
var BAR_COLORS = ["bg-emerald-500", "bg-[#3B82F6]", "bg-amber-500", "bg-purple-500", "bg-pink-500", "bg-teal-500"];

var PRODUCT_LABELS = {
  consorcio: "Consórcio",
  seguro: "Seguro",
  carta_contemplada: "Carta Contemplada",
};

function getProductIcon(type) {
  if (type === "consorcio") return Car;
  if (type === "seguro") return Shield;
  return Home;
}

function getProductColor(type) {
  if (type === "consorcio") return "#F26A4B";
  if (type === "seguro") return "#3B82F6";
  return "#10b981";
}

function TeamRow({ broker, idx }) {
  var badge = getBadge(broker.conversion);
  var initials = getInitials(broker.name);
  var avatarCls = AVATAR_COLORS[idx % AVATAR_COLORS.length];
  var barCls = BAR_COLORS[idx % BAR_COLORS.length];
  var barWidth = Math.min(broker.conversion, 100) + "%";
  var done = broker.meetings_done || 0;
  var scheduled = broker.meetings_scheduled || 0;

  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50">
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className={"w-8 h-8 rounded-full flex items-center justify-center " + avatarCls}>
            <span className="text-white text-xs font-semibold">{initials}</span>
          </div>
          <div>
            <p className="text-sm font-medium text-[#0A2F3D]">{broker.name}</p>
            <p className="text-xs text-gray-400">{broker.specialty || "Geral"}</p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-[#0A2F3D] font-medium">{broker.clients}</td>
      <td className="py-3 px-4 text-sm text-[#0A2F3D] font-medium">{broker.sales}</td>
      <td className="py-3 px-4 text-sm text-emerald-600 font-semibold">{formatValue(broker.commission)}</td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-emerald-600 font-medium" data-testid={"meetings-done-" + idx}>{done}</span>
          <span className="text-xs text-gray-300">/</span>
          <span className="text-xs text-blue-600 font-medium" data-testid={"meetings-scheduled-" + idx}>{scheduled}</span>
        </div>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={"h-full rounded-full " + barCls} style={{ width: barWidth }} />
          </div>
          <span className="text-xs text-gray-500">{broker.conversion}%</span>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className={"px-2 py-1 text-xs font-medium rounded-full " + badge.cls}>{badge.label}</span>
      </td>
    </tr>
  );
}

function FunnelItem({ item, maxCount }) {
  var Icon = getProductIcon(item.product_type);
  var color = getProductColor(item.product_type);
  var label = PRODUCT_LABELS[item.product_type] || item.product_type;
  var pct = maxCount > 0 ? (item.count / maxCount) * 100 : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: color }} />
          <span className="text-xs lg:text-sm font-medium text-gray-700">{label}</span>
        </div>
        <div className="flex items-center gap-2 lg:gap-4">
          <span className="text-xs lg:text-sm text-gray-500">{item.count} leads</span>
          <span className="text-xs lg:text-sm font-semibold text-[#0A2F3D]">{formatValue(item.total_value)}</span>
        </div>
      </div>
      <div className="h-2 lg:h-3 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: pct + "%", backgroundColor: color }} />
      </div>
    </div>
  );
}

export default function GestorDashboard() {
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];
  var teamState = useState([]);
  var team = teamState[0];
  var setTeam = teamState[1];
  var navigate = useNavigate();

  useEffect(function() {
    async function fetchAll() {
      try {
        var results = await Promise.all([
          api.get("/dashboard/gestor"),
          api.get("/dashboard/team"),
        ]);
        setData(results[0].data);
        setTeam(results[1].data.team || []);
      } catch (err) {
        console.error("Erro ao carregar dashboard:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="gestor-dashboard">
        <div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  var d = data || {};
  var funnel = d.funnel || [];
  var maxCount = 1;
  for (var i = 0; i < funnel.length; i++) {
    if (funnel[i].count > maxCount) maxCount = funnel[i].count;
  }

  return (
    <div data-testid="gestor-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
        <div onClick={function() { navigate("/clientes"); }} className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm cursor-pointer hover:shadow-md hover:ring-1 hover:ring-[#3B82F6]/20 transition-all" data-testid="kpi-card-clientes">
          <div className="flex items-start justify-between mb-3 lg:mb-4">
            <div className="p-2 lg:p-3 bg-[#3B82F6]/10 rounded-lg lg:rounded-xl">
              <Users className="w-5 h-5 lg:w-6 lg:h-6 text-[#3B82F6]" />
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D] mb-1" data-testid="kpi-clientes-ativos">{d.total_clients || 0}</p>
          <p className="text-gray-500 text-xs lg:text-sm">Clientes Ativos</p>
        </div>
        <div onClick={function() { navigate("/comissoes"); }} className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm cursor-pointer hover:shadow-md hover:ring-1 hover:ring-emerald-500/20 transition-all" data-testid="kpi-card-comissoes">
          <div className="flex items-start justify-between mb-3 lg:mb-4">
            <div className="p-2 lg:p-3 bg-emerald-500/10 rounded-lg lg:rounded-xl">
              <DollarSign className="w-5 h-5 lg:w-6 lg:h-6 text-emerald-500" />
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D] mb-1" data-testid="kpi-comissoes-total">{formatValue(d.total_commissions)}</p>
          <p className="text-gray-500 text-xs lg:text-sm">Comissões Totais</p>
        </div>
        <div onClick={function() { navigate("/equipe"); }} className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm cursor-pointer hover:shadow-md hover:ring-1 hover:ring-[#3B82F6]/20 transition-all" data-testid="kpi-card-corretores">
          <div className="flex items-start justify-between mb-3 lg:mb-4">
            <div className="p-2 lg:p-3 bg-[#3B82F6]/10 rounded-lg lg:rounded-xl">
              <User className="w-5 h-5 lg:w-6 lg:h-6 text-[#3B82F6]" />
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D] mb-1" data-testid="kpi-corretores">{d.total_brokers || 0}</p>
          <p className="text-gray-500 text-xs lg:text-sm">Corretores Ativos</p>
        </div>
        <div onClick={function() { navigate("/comissoes"); }} className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm cursor-pointer hover:shadow-md hover:ring-1 hover:ring-amber-500/20 transition-all" data-testid="kpi-card-risco">
          <div className="flex items-start justify-between mb-3 lg:mb-4">
            <div className="p-2 lg:p-3 bg-amber-500/10 rounded-lg lg:rounded-xl">
              <AlertTriangle className="w-5 h-5 lg:w-6 lg:h-6 text-amber-500" />
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold text-[#0A2F3D] mb-1" data-testid="kpi-comissoes-risco">{formatValue(d.commissions_at_risk)}</p>
          <p className="text-gray-500 text-xs lg:text-sm">Comissões em Risco</p>
        </div>
      </div>

      <DuplicatesAlert />

      {(d.overdue_clients > 0) && (
        <div onClick={function() { navigate("/pagamentos"); }}
          className="bg-red-50 border border-red-200 rounded-xl lg:rounded-2xl p-4 lg:p-5 mb-6 cursor-pointer hover:bg-red-100 transition-colors flex items-center gap-4"
          data-testid="overdue-payments-widget">
          <div className="p-3 bg-red-100 rounded-xl">
            <CreditCard className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-sm font-bold text-red-800">{d.overdue_clients} cliente{d.overdue_clients !== 1 ? "s" : ""} com pagamentos em atraso</p>
            <p className="text-xs text-red-600">{d.overdue_payments} pagamento{d.overdue_payments !== 1 ? "s" : ""} vencido{d.overdue_payments !== 1 ? "s" : ""} — Clique para ver detalhes</p>
          </div>
        </div>
      )}

      <AlertsWidget />
      <ReferralsWidget />
      <DoletasWidget />

      {team.length > 0 && (
        <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm mb-6 lg:mb-8" data-testid="team-performance">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <div>
              <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D]">Desempenho da Equipe</h3>
              <p className="text-xs lg:text-sm text-gray-500">Visão consolidada dos corretores</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Corretor</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Clientes</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Vendas</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Comissão</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider" title="Realizadas / Agendadas">Reuniões</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Conversão</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {team.map(function(broker, idx) {
                  return <TeamRow key={broker.id} broker={broker} idx={idx} />;
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mb-6 lg:mb-8">
        <MonthlyChart />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6 lg:mb-8">
        <div className="lg:col-span-2 bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <div>
              <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D]">Funil por Produto</h3>
              <p className="text-xs lg:text-sm text-gray-500">Todos os corretores</p>
            </div>
          </div>
          {funnel.length > 0 ? (
            <div className="space-y-4">
              {funnel.map(function(f) {
                return <FunnelItem key={f.product_type} item={f} maxCount={maxCount} />;
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400 text-sm">Nenhum lead cadastrado ainda</div>
          )}
        </div>

        <div className="bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-bold text-[#0A2F3D]">Resumo</h3>
          </div>
          <div className="space-y-3 lg:space-y-4">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <Users className="w-4 h-4 text-[#3B82F6]" />
              </div>
              <div className="min-w-0">
                <p className="text-xs lg:text-sm font-medium text-gray-800">{d.total_clients || 0} clientes ativos</p>
                <p className="text-xs text-gray-500">{d.total_brokers || 0} corretores na equipe</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
              <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                <DollarSign className="w-4 h-4 text-emerald-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs lg:text-sm font-medium text-gray-800">Comissões totais</p>
                <p className="text-xs text-gray-500">{formatValue(d.total_commissions)} acumulado</p>
              </div>
            </div>
            {d.commissions_at_risk > 0 && (
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-xl border border-red-100">
                <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs lg:text-sm font-medium text-gray-800">Comissões travadas</p>
                  <p className="text-xs text-gray-500">{formatValue(d.commissions_at_risk)} em risco</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
